import { z } from "zod";

import {
  MAX_NUM_RESULTS,
  MAX_RECS_RESULTS,
  PREVIOUS_STOREFRONT_PEEK,
  mergeProductCarouselOptions,
  mergeRecsOptions,
} from "./carousel-options.logic";
import type {
  CarouselSlidePaddingType,
  CarouselSlidesVisibleTypeDesktop,
  CarouselSlidesVisibleTypeMobile,
  CarouselSlidesVisibleTypeTablet,
  ReportBadBuilderValue,
} from "./types";

// Every schema that can meet a bad value is built by a factory taking a
// reporter, so parsing has no side effect beyond calling it; useBuilderOptions
// passes one that logs.

// Tablet overrides are three-way, so "same as mobile" can be told apart from
// "off" in the editor. showPeekTablet was a boolean before the list existed and
// never rendered, so stored booleans there read as "same as mobile", like unset.
export const tabletChoiceSchema = z.union([
  z.enum(["mobile", "on", "off"]),
  z.undefined(),
]);
export const legacyTabletChoiceSchema = z.union([
  tabletChoiceSchema,
  z.boolean(),
]);

export const parseTabletChoice = (
  report: ReportBadBuilderValue,
  field: string,
  value: unknown,
  { acceptsLegacyBoolean = false } = {},
) => {
  const parsed = (
    acceptsLegacyBoolean ? legacyTabletChoiceSchema : tabletChoiceSchema
  ).safeParse(value);
  if (!parsed.success) {
    report(field, parsed.error);
    return undefined;
  }
  return parsed.data === "on"
    ? true
    : parsed.data === "off"
      ? false
      : undefined;
};

// Absent is fine; a present value of the wrong shape is logged and dropped.
export const builderValue = <T extends z.ZodTypeAny>(
  report: ReportBadBuilderValue,
  field: string,
  schema: T,
) =>
  schema.optional().catch(({ error, input }) => {
    // Re-parsed so issue paths start at this field, not at the block's options.
    report(field, schema.safeParse(input).error ?? error);
    return undefined;
  });

// Counts and padding: one of the values the carousel has a class for, stored as
// a string or a number; an empty string counts as unset.
const layoutValue = <T extends string>(values: readonly [T, ...T[]]) =>
  z
    .union([z.string(), z.number()])
    .transform(value => String(value) || undefined)
    .pipe(z.enum(values).optional());
const desktopSlides = layoutValue([
  "1",
  "2",
  "3",
  "3.25",
  "4",
  "5",
  "6",
] as const satisfies readonly CarouselSlidesVisibleTypeDesktop[]);
const tabletSlides = layoutValue([
  "1",
  "2",
  "3",
  "4",
] as const satisfies readonly CarouselSlidesVisibleTypeTablet[]);
const mobileSlides = layoutValue([
  "1",
  "2",
  "2.25",
  "3",
] as const satisfies readonly CarouselSlidesVisibleTypeMobile[]);
const padding = layoutValue([
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
] as const satisfies readonly CarouselSlidePaddingType[]);
const slideSchemas = {
  onDesktop: desktopSlides,
  onTablet: tabletSlides,
  onMobile: mobileSlides,
};
const paddingSchemas = {
  onDesktop: padding,
  onTablet: padding,
  onMobile: padding,
};

// The previous storefront stored layout per breakpoint as one nested object, and
// live content still does. Both shapes parse to { onDesktop, onTablet, onMobile }.
export const layoutOption = <
  D extends z.ZodTypeAny,
  T extends z.ZodTypeAny,
  M extends z.ZodTypeAny,
>(
  report: ReportBadBuilderValue,
  field: string,
  schemas: { onDesktop: D; onTablet: T; onMobile: M },
  defaults: {
    onDesktop?: z.output<D>;
    onTablet?: z.output<T>;
    onMobile?: z.output<M>;
  } = {},
) =>
  builderValue(
    report,
    field,
    z.union([
      schemas.onDesktop.transform((onDesktop: z.output<D>) => ({
        onDesktop,
        onTablet: undefined,
        onMobile: undefined,
      })),
      z
        .object({
          onDesktop: schemas.onDesktop.optional(),
          onTablet: schemas.onTablet.optional(),
          onMobile: schemas.onMobile.optional(),
        })
        .transform(nested => ({
          onDesktop: nested.onDesktop ?? defaults.onDesktop,
          onTablet: nested.onTablet ?? defaults.onTablet,
          onMobile: nested.onMobile ?? defaults.onMobile,
        })),
    ]),
  );

// A product count, stored as a number or a digit string, held to 1..max.
const countSchema = (max: number) =>
  z
    .union([
      z.number(),
      z.string().regex(/^\d+$/, "Expected a whole number").transform(Number),
    ])
    .transform(count => Math.min(Math.max(count, 1), max));
const numResultsSchema = countSchema(MAX_NUM_RESULTS);

export const createProductCarouselStoredSchema = (
  report: ReportBadBuilderValue,
) => {
  const carouselValue = <T extends z.ZodTypeAny>(field: string, schema: T) =>
    builderValue(report, `ProductCarousel.${field}`, schema);
  return z.object({
    // An empty or null category means none picked yet, not bad data.
    categoryId: carouselValue("categoryId", z.string().nullable()),
    productCategory: carouselValue(
      "productCategory",
      z.object({ options: z.object({ category: z.string() }) }).nullable(),
    ),
    numResults: carouselValue("numResults", numResultsSchema),
    resultCount: carouselValue("resultCount", numResultsSchema),
    slidesVisible: layoutOption(
      report,
      "ProductCarousel.slidesVisible",
      slideSchemas,
    ),
    slidesVisibleTablet: carouselValue("slidesVisibleTablet", tabletSlides),
    slidesVisibleMobile: carouselValue("slidesVisibleMobile", mobileSlides),
    showPeek: layoutOption(
      report,
      "ProductCarousel.showPeek",
      { onDesktop: z.boolean(), onTablet: z.boolean(), onMobile: z.boolean() },
      PREVIOUS_STOREFRONT_PEEK,
    ),
    showPeekTablet: z.unknown().transform(value =>
      parseTabletChoice(report, "ProductCarousel.showPeekTablet", value, {
        acceptsLegacyBoolean: true,
      }),
    ),
    showPeekMobile: carouselValue("showPeekMobile", z.boolean()),
    slidePadding: layoutOption(
      report,
      "ProductCarousel.slidePadding",
      paddingSchemas,
    ),
    slidePaddingTablet: carouselValue("slidePaddingTablet", padding),
    slidePaddingMobile: carouselValue("slidePaddingMobile", padding),
    showNavigationArrowsTablet: z
      .unknown()
      .transform(value =>
        parseTabletChoice(
          report,
          "ProductCarousel.showNavigationArrowsTablet",
          value,
        ),
      ),
  });
};

export const createProductCarouselOptionsSchema = (
  report: ReportBadBuilderValue,
) =>
  createProductCarouselStoredSchema(report).transform(
    mergeProductCarouselOptions,
  );

// The first entry is the default: today's gray bar.
export const HEADING_STYLES = ["bar", "headline"] as const;
const headingStyleSchema = (report: ReportBadBuilderValue) =>
  z
    .enum(HEADING_STYLES)
    .optional()
    .catch(({ error, input }) => {
      report(
        "productRecommendations.headingStyle",
        z.enum(HEADING_STYLES).safeParse(input).error ?? error,
      );
      return undefined;
    })
    .transform(style => style ?? HEADING_STYLES[0]);

// Options shared by both recs blocks, so their Options tabs match. A present
// value of the wrong shape is reported and treated as unset.
export const createRecsStoredSchema = (report: ReportBadBuilderValue) => {
  const recsValue = <T extends z.ZodTypeAny>(field: string, schema: T) =>
    builderValue(report, `productRecommendations.${field}`, schema);
  return z.object({
    numResults: recsValue("numResults", countSchema(MAX_RECS_RESULTS)),
    heading: recsValue("heading", z.string()),
    enableHeadline: recsValue("enableHeadline", z.boolean()),
    headingStyle: headingStyleSchema(report),
    // The older block stores slides as { onDesktop, onMobile }, the newer one flat.
    slidesVisible: layoutOption(
      report,
      "productRecommendations.slidesVisible",
      slideSchemas,
    ),
    slidesVisibleTablet: recsValue("slidesVisibleTablet", tabletSlides),
    slidesVisibleMobile: recsValue("slidesVisibleMobile", mobileSlides),
    showPeek: recsValue("showPeek", z.boolean()),
    showPeekTablet: z.unknown().transform(value =>
      parseTabletChoice(
        report,
        "productRecommendations.showPeekTablet",
        value,
        {
          acceptsLegacyBoolean: true,
        },
      ),
    ),
    showPeekMobile: recsValue("showPeekMobile", z.boolean()),
    slidePadding: recsValue("slidePadding", padding),
    slidePaddingTablet: recsValue("slidePaddingTablet", padding),
    slidePaddingMobile: recsValue("slidePaddingMobile", padding),
    enableNavigationArrows: recsValue("enableNavigationArrows", z.boolean()),
    showNavigationArrowsMobile: recsValue(
      "showNavigationArrowsMobile",
      z.boolean(),
    ),
    showNavigationArrowsTablet: z
      .unknown()
      .transform(value =>
        parseTabletChoice(
          report,
          "productRecommendations.showNavigationArrowsTablet",
          value,
        ),
      ),
    showNavigationArrowsDesktop: recsValue(
      "showNavigationArrowsDesktop",
      z.boolean(),
    ),
    enableBasicView: recsValue("enableBasicView", z.boolean()),
    productTileBorderColor: recsValue("productTileBorderColor", z.string()),
    enableAddToCart: recsValue("enableAddToCart", z.boolean()),
    enableQuickView: recsValue("enableQuickView", z.boolean()),
    enableFavorites: recsValue("enableFavorites", z.boolean()),
    enableLowStockBadge: recsValue("enableLowStockBadge", z.boolean()),
    ButtonNavigationColor: recsValue("ButtonNavigationColor", z.string()),
    ctaText: recsValue("ctaText", z.string()),
    ctaHref: recsValue("ctaHref", z.string()),
  });
};

export const createRecsOptionsSchema = (report: ReportBadBuilderValue) =>
  createRecsStoredSchema(report).transform(mergeRecsOptions);
